'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sparkles } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

export default function NotFound() {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF7F2' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link href="/fashion-discovery-interface" className="flex items-center gap-2 transition-opacity hover:opacity-70">
              <AppLogo size={24} />
              <span className="font-serif-display text-lg sm:text-xl tracking-tight" style={{ color: '#1A1A1A' }}>
                Atelier.ai
              </span>
            </Link>
            <Link
              href="/fashion-discovery-interface"
              className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
            >
              <ArrowLeft size={15} />
              <span>Back to Atelier.ai</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-16 sm:py-24">
        <div className="w-full max-w-lg text-center">

          {/* Issue label */}
          <div className="flex items-center justify-center gap-2 mb-8 animate-fade-in-up stagger-1">
            <Sparkles size={14} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>
              Atelier.ai — Error 404
            </span>
            <Sparkles size={14} style={{ color: '#C9A96E' }} />
          </div>

          {/* Large editorial 404 */}
          <div className="relative mb-6 animate-fade-in-up stagger-2">
            <p
              className="font-serif-display leading-none select-none"
              style={{
                fontSize: 'clamp(7rem, 22vw, 14rem)',
                color: 'transparent',
                WebkitTextStroke: '1.5px hsl(35 20% 82%)',
                letterSpacing: '-0.02em',
              }}
              aria-hidden="true"
            >
              404
            </p>
            {/* Overlapping accent line */}
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{ bottom: '18%', width: '40%', height: '2px', backgroundColor: '#C9A96E', opacity: 0.5 }}
            />
          </div>

          {/* Headline */}
          <h1 className="font-serif-display text-3xl sm:text-4xl mb-4 leading-tight animate-fade-in-up stagger-3" style={{ color: '#1A1A1A' }}>
            This page is off the rack
          </h1>

          {/* Subtext */}
          <p className="text-base sm:text-lg leading-relaxed mb-10 animate-fade-in-up stagger-4" style={{ color: 'hsl(30 8% 52%)' }}>
            The page you're looking for has stepped out — or perhaps it never made it to the floor. Let's get you back to something beautiful.
          </p>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-10 animate-fade-in-up stagger-4">
            <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />
            <span className="text-xs tracking-widest uppercase" style={{ color: 'hsl(35 20% 78%)' }}>✦</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />
          </div>

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-in-up stagger-5">
            <Link
              href="/fashion-discovery-interface"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all hover:opacity-85 active:scale-95"
              style={{ backgroundColor: '#1A1A1A', color: '#FAF7F2' }}
            >
              <ArrowLeft size={15} />
              Back to Home
            </Link>
            <Link
              href="/waitlist"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium tracking-wide border transition-all hover:opacity-75 active:scale-95"
              style={{ borderColor: 'hsl(35 20% 82%)', color: 'hsl(30 8% 38%)', backgroundColor: 'transparent' }}
            >
              Join the Waitlist
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t text-center" style={{ borderColor: 'hsl(35 20% 88%)' }}>
        <p className="text-xs tracking-widest uppercase" style={{ color: 'hsl(35 20% 78%)' }}>
          Atelier.ai — Your AI Personal Stylist
        </p>
      </footer>
    </div>
  );
}